import { isWeb } from '@gluestack-ui/nativewind-utils/IsWeb';
import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation } from '@tanstack/react-query';
import { useFocusEffect, useLocalSearchParams, useRouter, Href } from 'expo-router';
import { useAtom } from 'jotai';
import { AlertCircleIcon, EyeIcon, EyeOffIcon } from 'lucide-react-native';
import { useCallback, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { KeyboardAwareScrollView } from 'react-native-keyboard-controller';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { z } from 'zod';

import { Header } from '@/components/header';
import { Heading } from '@/components/heading';
import { Button, ButtonSpinner, ButtonText } from '@/components/ui/button';
import {
  FormControl,
  FormControlError,
  FormControlErrorIcon,
  FormControlErrorText,
  FormControlLabel,
  FormControlLabelText,
} from '@/components/ui/form-control';
import { Input, InputField, InputIcon, InputSlot } from '@/components/ui/input';
import {
  Modal,
  ModalBackdrop,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
} from '@/components/ui/modal';
import { Pressable } from '@/components/ui/pressable';
import { Text } from '@/components/ui/text';
import { VStack } from '@/components/ui/vstack';
import { ANALYTICS_EVENTS } from '@/constants/analytics';
import { useColors } from '@/hooks/useColors';
import { useToaster } from '@/hooks/useToaster';
import { redirectAfterConfirmationAtom } from '@/services/globalState';
import { updatePassword } from '@/services/updatePassword';
import { segmentClient } from '@/util/analytics';

const formSchema = z
  .object({
    password: z
      .string()
      .min(8, 'Password must be at least 8 characters')
      .regex(/[A-Z]/, 'Password must include an uppercase letter')
      .regex(/[a-z]/, 'Password must include a lowercase letter')
      .regex(/[0-9]/, 'Password must include a number'),
    confirmPassword: z.string(),
  })
  .refine((data) => data.password === data.confirmPassword, {
    message: 'Passwords do not match',
    path: ['confirmPassword'],
  });

const passwordRules = 'minlength: 8; required: lower; required: upper; required: digit;';

export default function UpdatePassword() {
  const { colors } = useColors();

  const { bottom } = useSafeAreaInsets();

  const toast = useToaster();

  const { replace } = useRouter();

  const [redirectAfterConfirmation, setRedirectAfterConfirmation] = useAtom(
    redirectAfterConfirmationAtom
  );

  const { fromConfirmAccountDetailsPage } = useLocalSearchParams<{
    fromConfirmAccountDetailsPage?: string;
  }>();

  const isFromConfirmation = fromConfirmAccountDetailsPage === 'true';

  const defaultDestination = '/(secure)/(tabs)/directory';

  const {
    control,
    handleSubmit,
    formState: { errors },
  } = useForm({
    defaultValues: {
      password: '',
      confirmPassword: '',
    },
    resolver: zodResolver(formSchema),
  });

  const [showPassword, setShowPassword] = useState(false);

  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const [showSkipWarning, setShowSkipWarning] = useState(false);

  useFocusEffect(
    useCallback(() => {
      segmentClient.track(ANALYTICS_EVENTS.UPDATE_PASSWORD_PAGE_VIEWED);
    }, [])
  );

  const { mutateAsync: updatePasswordMutation, isPending } = useMutation({
    mutationFn: (newPassword: string) => updatePassword(newPassword),
    onSuccess: () => {
      toast.showSuccess({
        title: 'Password updated!',
        message: 'Your new password has been saved.',
        duration: 5000,
      });

      segmentClient.track(ANALYTICS_EVENTS.UPDATE_PASSWORD_PAGE_SUBMITTED);

      const destination = redirectAfterConfirmation || defaultDestination;

      setRedirectAfterConfirmation(null);

      replace(destination as Href);
    },
    onError: (error) => {
      toast.showError({
        title: 'Could not update password',
        apiError: { error, errorPatternMatcher: {} },
      });
    },
  });

  const onSubmit = handleSubmit(async ({ password }) => {
    await updatePasswordMutation(password);
  });

  const handleSkip = () => {
    toast.showSuccess({
      title: 'Welcome to Primum!',
      message: 'Your account is all set up and ready to go.',
      duration: 5000,
    });

    segmentClient.track(ANALYTICS_EVENTS.UPDATE_PASSWORD_PAGE_SKIPPED);

    const destination = redirectAfterConfirmation || defaultDestination;

    setRedirectAfterConfirmation(null);

    replace(destination as Href);
  };

  const handleSkipConfirmed = () => {
    setShowSkipWarning(false);

    handleSkip();
  };

  return (
    <VStack
      className="flex-1 bg-background-0 md:bg-transparent"
      style={{
        paddingBottom: bottom,
      }}>
      <Header
        contentScrollsUnderHeader
        title={isFromConfirmation ? 'Set Your Password' : 'Update Your Password'}
      />
      <KeyboardAwareScrollView
        contentInsetAdjustmentBehavior="automatic"
        mode="layout"
        bottomOffset={12}
        keyboardShouldPersistTaps="handled"
        keyboardDismissMode="on-drag"
        contentContainerClassName="flex-col flex-grow items-center justify-start px-5 pt-8 pb-10 web:pb-12 md:px-0">
        <VStack className="w-full max-w-[540px] md:px-5">
          <Text size="md" className="mb-2 self-stretch text-center font-normal text-typography-900">
            {isFromConfirmation
              ? 'Set a password for your Primum account so you can easily log in.'
              : 'Update the password for your Primum account.'}
          </Text>
        </VStack>
        <VStack className="mt-4 w-full max-w-[540px] md:mt-0">
          <VStack className="md:shadow-soft w-full gap-6 md:rounded-lg md:bg-background-0 md:px-5 md:py-8">
            <VStack className="w-full gap-1">
              <Text size="sm" className="font-normal text-typography-600">
                Password requirements:
              </Text>
              <Text size="sm" className="font-normal text-typography-600">
                {' '}
                {'\u2022'} Must be a minimum of 8 characters
              </Text>
              <Text size="sm" className="font-normal text-typography-600">
                {' '}
                {'\u2022'} Must include an uppercase letter
              </Text>
              <Text size="sm" className="font-normal text-typography-600">
                {' '}
                {'\u2022'} Must include a lowercase letter
              </Text>
              <Text size="sm" className="mb-2 font-normal text-typography-600">
                {' '}
                {'\u2022'} Must include a number
              </Text>
            </VStack>
            <Controller
              name="password"
              control={control}
              render={({ field: { onChange, value, onBlur } }) => (
                <FormControl isInvalid={!!errors.password?.message}>
                  <FormControlLabel className="mb-2">
                    <FormControlLabelText>
                      {isFromConfirmation ? 'Password' : 'New Password'}
                    </FormControlLabelText>
                  </FormControlLabel>
                  <Input>
                    <InputField
                      autoCorrect={false}
                      autoCapitalize="none"
                      style={{ color: colors.typography[900] }}
                      className="text-base"
                      value={value}
                      onChangeText={onChange}
                      onBlur={onBlur}
                      type={showPassword ? 'text' : 'password'}
                      passwordRules={passwordRules}
                      autoComplete={isWeb ? ('new-password' as any) : 'password-new'}
                    />
                    <InputSlot className="pr-3" onPress={() => setShowPassword((prev) => !prev)}>
                      <InputIcon as={showPassword ? EyeIcon : EyeOffIcon} />
                    </InputSlot>
                  </Input>
                  <FormControlError className="mt-2">
                    <FormControlErrorIcon size="xs" as={AlertCircleIcon} />
                    <FormControlErrorText size="xs">
                      {errors.password?.message}
                    </FormControlErrorText>
                  </FormControlError>
                </FormControl>
              )}
            />
            <Controller
              name="confirmPassword"
              control={control}
              render={({ field: { onChange, value, onBlur } }) => (
                <FormControl isInvalid={!!errors.confirmPassword?.message}>
                  <FormControlLabel className="mb-2">
                    <FormControlLabelText>Confirm Password</FormControlLabelText>
                  </FormControlLabel>
                  <Input>
                    <InputField
                      autoCorrect={false}
                      autoCapitalize="none"
                      style={{ color: colors.typography[900] }}
                      className="text-base"
                      value={value}
                      onChangeText={onChange}
                      onBlur={onBlur}
                      type={showConfirmPassword ? 'text' : 'password'}
                      passwordRules={passwordRules}
                      autoComplete={isWeb ? ('new-password' as any) : 'password-new'}
                    />
                    <InputSlot
                      className="pr-3"
                      onPress={() => setShowConfirmPassword((prev) => !prev)}>
                      <InputIcon as={showConfirmPassword ? EyeIcon : EyeOffIcon} />
                    </InputSlot>
                  </Input>
                  <FormControlError className="mt-2">
                    <FormControlErrorIcon size="xs" as={AlertCircleIcon} />
                    <FormControlErrorText size="xs">
                      {errors.confirmPassword?.message}
                    </FormControlErrorText>
                  </FormControlError>
                </FormControl>
              )}
            />
          </VStack>
        </VStack>
        <VStack className="mt-8 w-full max-w-[540px] md:mt-3 md:px-5">
          <Button className="w-full gap-x-2" onPress={onSubmit} disabled={isPending}>
            {isPending ? <ButtonSpinner color={colors.primary[200]} /> : null}
            <ButtonText size="sm">
              {isFromConfirmation ? 'Set Password' : 'Update Password'}
            </ButtonText>
          </Button>
        </VStack>
        <VStack className="mt-4 w-full max-w-[540px] items-center md:px-5">
          <Pressable onPress={() => setShowSkipWarning(true)}>
            <Text size="sm" className="font-medium text-primary-500">
              Skip for now
            </Text>
          </Pressable>
        </VStack>
      </KeyboardAwareScrollView>
      <Modal isOpen={showSkipWarning} size="md" onClose={() => setShowSkipWarning(false)}>
        <ModalBackdrop />
        <ModalContent>
          <ModalHeader className="pb-3">
            <Heading size="h2-md" className="text-typography-900">
              Skip setting a password?
            </Heading>
          </ModalHeader>
          <ModalBody className="pb-4">
            <Text className="text-center text-typography-600">
              If you skip setting a password, you will need to go through the &ldquo;Forgot
              Password&rdquo; flow the next time you want to access your account.{'\n\n'}We
              recommend setting a password now.
            </Text>
          </ModalBody>
          <ModalFooter className="flex-col gap-3 pt-2">
            <Button className="w-full" onPress={() => setShowSkipWarning(false)}>
              <ButtonText size="sm">Go Back and Set a Password</ButtonText>
            </Button>
            <Pressable onPress={handleSkipConfirmed}>
              <Text size="sm" className="font-medium text-primary-500">
                Yes, skip for now
              </Text>
            </Pressable>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </VStack>
  );
}
